/** Builder, so a type's `filters.ts` is a readable table and not a wall of object literals. */
export function condition(label, operator, value, extra = {}) {
    return {
        id: extra.id ?? (extra.compareAttribute ? `${operator}:${extra.compareAttribute}` : operator),
        label,
        operator,
        value,
        ...(extra.compareAttribute === undefined ? {} : { compareAttribute: extra.compareAttribute }),
    };
}
/** The distinct operators a condition list uses — what `filterOperators` must be. */
export function operatorsOf(conditions) {
    const seen = [];
    for (const entry of conditions)
        if (!seen.includes(entry.operator))
            seen.push(entry.operator);
    return seen;
}
/** The condition a rule came from, so the pane can re-select the right dropdown row. */
export function findCondition(conditions, operator, compareAttribute) {
    return (conditions.find((entry) => entry.operator === operator && (entry.compareAttribute ?? undefined) === compareAttribute) ?? conditions.find((entry) => entry.operator === operator));
}
/* ── the shared rows ──────────────────────────────────────────────────────── */
/**
 * Rows that appear on many types with the same wording. A type's `filters.ts` composes these
 * and adds its own; nothing here is a default that a type gets without asking for it — the
 * whole point of the file is that the list is measured per type.
 */
export const IS_EMPTY = condition('is empty', 'is_empty', 'none');
export const IS_NOT_EMPTY = condition('is not empty', 'is_not_empty', 'none');
export const CONTAINS = condition('contains', 'contains_text', 'text');
export const NOT_CONTAINS = condition("doesn't contain", 'not_contains_text', 'text');
export const STARTS_WITH = condition('starts with', 'starts_with', 'text');
/** `is` / `is not` over the column's own vocabulary (status labels, options, people, …). */
export const IS_ONE_OF = condition('is', 'any_of', 'options');
export const IS_NOT_ONE_OF = condition('is not', 'not_any_of', 'options');
/** The text/free-value form of `is` — a `text`, `link`, `email`, `phone`, `formula` column. */
export const IS_TEXT = condition('is', 'any_of', 'text');
export const IS_NOT_TEXT = condition('is not', 'not_any_of', 'text');
/**
 * `text is` / `text is not` on `status`, `dropdown` and `mirror`: compare the **label text**
 * rather than the stored index, which is what `compare_attribute: "text"` means `[sdl]`.
 */
export const TEXT_IS = condition('text is', 'any_of', 'text', { compareAttribute: 'text' });
export const TEXT_IS_NOT = condition('text is not', 'not_any_of', 'text', { compareAttribute: 'text' });
/** The six date comparisons, in monday's measured order for a plain `date` column. */
export const DATE_ROWS = [
    condition('in the next', 'within_the_next', 'days'),
    condition('in the last', 'within_the_last', 'days'),
    condition('is between', 'between', 'dateRange'),
    condition('is on or after', 'greater_than_or_equals', 'date'),
    condition('is after', 'greater_than', 'date'),
    condition('is on or before', 'lower_than_or_equal', 'date'),
    condition('is before', 'lower_than', 'date'),
];
//# sourceMappingURL=filter-conditions.js.map